// src/pages/inventory/InventoryItems.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Paper,
    InputAdornment,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    FilterList as FilterIcon,
    Search as SearchIcon,
    Inventory as InventoryIcon,
    CheckCircle as CheckCircleIcon,
    Category as CategoryIcon,
    Close as CloseIcon,
    MonetizationOn as MoneyIcon,
    Refresh as RefreshIcon,
    LocalOffer as TagIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';

const InventoryItems = ({ openNewOnLoad = false }) => {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        category_id: '',
        name: '',
        model: '',
        brand: '',
        description: '',
        sku: '',
        barcode: '',
        purchase_price: '',
        selling_price: '',
        rental_price: '',
        rental_type: 'monthly',
        rental_installments: '',
        min_stock: 1,
        equipment_type: '',
        frequency: '',
        speed_capacity: '',
        hardware_firmware_version: ''
    });

    const [filterCategory, setFilterCategory] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [stockFilter, setStockFilter] = useState('all'); // 'all', 'low', 'in_stock'

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Fetch items
            const itemsResponse = await fetch('/api/inventory/items', { headers });
            if (!itemsResponse.ok) {
                throw new Error('Error al obtener productos');
            }
            const itemsData = await itemsResponse.json();
            setItems(itemsData);

            // Fetch categories
            const categoriesResponse = await fetch('/api/inventory/categories', { headers });
            if (!categoriesResponse.ok) {
                throw new Error('Error al obtener categorías');
            }
            const categoriesData = await categoriesResponse.json();
            setCategories(categoriesData);

        } catch (err) {
            console.error('Error fetching inventory items data:', err);
            setError(err.message || 'Error al cargar productos de inventario');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        if (openNewOnLoad || location?.pathname?.endsWith('/new') || location?.search?.includes('action=new')) {
            handleOpenDialog();
        }
    }, [fetchData, location?.pathname, location?.search, openNewOnLoad]);

    const handleOpenDialog = (item = null) => {
        if (item) {
            setCurrentItem({
                ...item,
                purchase_price: parseFloat(item.purchase_price) || '',
                selling_price: parseFloat(item.selling_price) || '',
                rental_price: item.rental_price !== null && item.rental_price !== undefined ? (parseFloat(item.rental_price) || 0) : '',
                rental_type: item.rental_type || 'monthly',
                rental_installments: item.rental_installments || '',
                min_stock: parseInt(item.min_stock) || 1,
                equipment_type: item.equipment_type || '',
                frequency: item.frequency || '',
                speed_capacity: item.speed_capacity || '',
                hardware_firmware_version: item.hardware_firmware_version || ''
            });
            setIsEditing(true);
        } else {
            setCurrentItem({
                category_id: '',
                name: '',
                model: '',
                brand: '',
                description: '',
                sku: '',
                barcode: '',
                purchase_price: '',
                selling_price: '',
                rental_price: '',
                rental_type: 'monthly',
                rental_installments: '',
                min_stock: 1,
                equipment_type: '',
                frequency: '',
                speed_capacity: '',
                hardware_firmware_version: ''
            });
            setIsEditing(false);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentItem({
            category_id: '',
            name: '',
            model: '',
            brand: '',
            description: '',
            sku: '',
            barcode: '',
            purchase_price: '',
            selling_price: '',
            rental_price: '',
            rental_type: 'monthly',
            rental_installments: '',
            min_stock: 1,
            equipment_type: '',
            frequency: '',
            speed_capacity: '',
            hardware_firmware_version: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentItem(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing
                ? `/api/inventory/items/${currentItem.id}`
                : '/api/inventory/items';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(currentItem)
            });

            if (!response.ok) {
                const errJson = await response.json().catch(() => ({}));
                throw new Error(errJson.message || `Error al ${isEditing ? 'actualizar' : 'crear'} producto`);
            }

            handleCloseDialog();
            fetchData();
        } catch (err) {
            console.error('Error submitting item:', err);
            setError(err.message || `Error al ${isEditing ? 'actualizar' : 'crear'} producto`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este producto? Esta acción no se puede deshacer.')) {
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await fetch(`/api/inventory/items/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errJson = await response.json().catch(() => ({}));
                throw new Error(errJson.message || 'Error al eliminar producto');
            }

            fetchData();
        } catch (err) {
            console.error('Error deleting item:', err);
            setError(err.message || 'Error al eliminar producto');
        } finally {
            setLoading(false);
        }
    };

    // Filter Items
    const filteredItems = items.filter(item => {
        if (filterCategory && item.category_id !== filterCategory) {
            return false;
        }

        if (stockFilter === 'low') {
            if ((item.total_stock || 0) > (item.min_stock || 0)) return false;
        } else if (stockFilter === 'in_stock') {
            if ((item.total_stock || 0) <= (item.min_stock || 0)) return false;
        }

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const nameMatch = item.name && item.name.toLowerCase().includes(searchLower);
            const modelMatch = item.model && item.model.toLowerCase().includes(searchLower);
            const brandMatch = item.brand && item.brand.toLowerCase().includes(searchLower);
            const skuMatch = item.sku && item.sku.toLowerCase().includes(searchLower);

            if (!(nameMatch || modelMatch || brandMatch || skuMatch)) {
                return false;
            }
        }

        return true;
    });

    // Stats calculations
    const totalCatalogItems = items.length;
    const lowStockCount = items.filter(i => (i.total_stock || 0) <= (i.min_stock || 0)).length;
    const totalInventoryValue = items.reduce((acc, curr) => acc + ((curr.total_stock || 0) * (parseFloat(curr.purchase_price) || 0)), 0);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading && items.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '65vh', gap: 2 }}>
                <CircularProgress size={48} thickness={4} sx={{ color: '#2563eb' }} />
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Cargando catálogo de productos...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3.5 }, bgcolor: '#f8fafc', minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Header Hero Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                    <Box sx={{
                        width: { xs: 44, md: 52 },
                        height: { xs: 44, md: 52 },
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: '0 8px 16px rgba(37, 99, 235, 0.25)',
                        flexShrink: 0
                    }}>
                        <InventoryIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                                Productos de Inventario
                            </Typography>
                            <Chip
                                label={`${totalCatalogItems} Productos`}
                                size="small"
                                sx={{ bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', fontWeight: 800, fontSize: '11px', borderRadius: '6px' }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.2, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                            Catálogo maestro de equipos, precios y stock
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'nowrap', width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="contained"
                        onClick={() => handleOpenDialog()}
                        startIcon={<AddIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            borderRadius: '9px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 2.5,
                            py: 1,
                            width: { xs: '100%', sm: 'auto' },
                            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
                            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)' }
                        }}
                    >
                        Nuevo Producto
                    </Button>

                    <Tooltip title="Actualizar lista">
                        <IconButton
                            onClick={() => fetchData(true)}
                            disabled={refreshing}
                            sx={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '9px',
                                bgcolor: '#fff',
                                p: 1,
                                flexShrink: 0,
                                '&:hover': { bgcolor: '#f1f5f9' }
                            }}
                        >
                            <RefreshIcon sx={{
                                color: '#475569',
                                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Quick Metrics Bar */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        TOTAL PRODUCTOS
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {totalCatalogItems} <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>en {categories.length} categorías</Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <TagIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: '12px', border: lowStockCount > 0 ? '1px solid #fde68a' : '1px solid #e2e8f0', bgcolor: lowStockCount > 0 ? '#fffbeb' : '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: lowStockCount > 0 ? '#b45309' : '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        STOCK BAJO / ALERTA
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: lowStockCount > 0 ? '#d97706' : '#059669' }}>
                                        {lowStockCount} <Typography component="span" variant="caption" sx={{ color: lowStockCount > 0 ? '#b45309' : '#64748b' }}>requieren atención</Typography>
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: lowStockCount > 0 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.1)', color: lowStockCount > 0 ? '#d97706' : '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <WarningIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <CardContent sx={{ py: 1.8, px: 2, '&:last-child': { pb: 1.8 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', fontSize: '10px' }}>
                                        VALOR DE INVENTARIO
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669' }}>
                                        ${totalInventoryValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MoneyIcon sx={{ fontSize: 20 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filter & Search Bar */}
            <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            placeholder="Buscar por nombre, modelo, marca o SKU..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#94a3b8' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <ClearIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '8px', bgcolor: '#fff' }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3.5}>
                        <TextField
                            fullWidth
                            select
                            label="Categoría"
                            size="small"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FilterIcon sx={{ color: '#94a3b8' }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: '8px', bgcolor: '#fff' }
                            }}
                        >
                            <MenuItem value="">Todas las categorías ({items.length})</MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3.5}>
                        <Box sx={{ display: 'flex', gap: 0.8, overflowX: 'auto', py: 0.2 }}>
                            <Chip
                                label="Todos"
                                onClick={() => setStockFilter('all')}
                                color={stockFilter === 'all' ? 'primary' : 'default'}
                                variant={stockFilter === 'all' ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 700, borderRadius: '8px', flex: 1, fontSize: '11px' }}
                            />
                            <Chip
                                label="Stock Bajo"
                                onClick={() => setStockFilter('low')}
                                color={stockFilter === 'low' ? 'warning' : 'default'}
                                variant={stockFilter === 'low' ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 700, borderRadius: '8px', flex: 1, fontSize: '11px' }}
                            />
                            <Chip
                                label="Óptimo"
                                onClick={() => setStockFilter('in_stock')}
                                color={stockFilter === 'in_stock' ? 'success' : 'default'}
                                variant={stockFilter === 'in_stock' ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 700, borderRadius: '8px', flex: 1, fontSize: '11px' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* DESKTOP VIEW: Custom Table (Visible on md and up) */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Producto / Modelo</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Marca</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Categoría</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Nivel de Stock</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>P. Compra</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>P. Venta</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>P. Alquiler / Cuota</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', py: 1.8 }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedItems.length > 0 ? (
                                    paginatedItems.map((item) => {
                                        const stockNum = item.total_stock || 0;
                                        const minStockNum = item.min_stock || 0;
                                        const isLowStock = stockNum <= minStockNum;
                                        const isOutOfStock = stockNum === 0;

                                        const pPrice = parseFloat(item.purchase_price) || 0;
                                        const sPrice = parseFloat(item.selling_price) || 0;
                                        const rPrice = parseFloat(item.rental_price) || 0;
                                        const rType = item.rental_type || 'monthly';
                                        const rInst = item.rental_installments;

                                        return (
                                            <TableRow key={item.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                                            {item.name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                                                            {item.model && (
                                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, bgcolor: '#f1f5f9', px: 0.8, py: 0.2, borderRadius: '4px' }}>
                                                                    Mod: {item.model}
                                                                </Typography>
                                                            )}
                                                            {item.sku && (
                                                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                                    SKU: {item.sku}
                                                                </Typography>
                                                            )}
                                                            {item.equipment_type && (
                                                                <Chip
                                                                    label={item.equipment_type}
                                                                    size="small"
                                                                    sx={{ height: 18, fontSize: '9px', fontWeight: 700, bgcolor: 'rgba(37, 99, 235, 0.08)', color: '#2563eb' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                <TableCell>
                                                    {item.brand ? (
                                                        <Chip
                                                            label={item.brand}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontWeight: 700, fontSize: '11px', borderColor: '#cbd5e1', color: '#334155' }}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    <Chip
                                                        icon={<CategoryIcon sx={{ fontSize: '12px !important' }} />}
                                                        label={item.category_name || 'Sin Categoría'}
                                                        size="small"
                                                        sx={{ height: 24, fontSize: '11px', fontWeight: 600, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569' }}
                                                    />
                                                </TableCell>

                                                <TableCell>
                                                    {isOutOfStock ? (
                                                        <Chip
                                                            icon={<WarningIcon sx={{ fontSize: '12px !important' }} />}
                                                            label="Agotado (0)"
                                                            size="small"
                                                            color="error"
                                                            sx={{ fontWeight: 800, fontSize: '11px', borderRadius: '6px' }}
                                                        />
                                                    ) : isLowStock ? (
                                                        <Chip
                                                            icon={<WarningIcon sx={{ fontSize: '12px !important' }} />}
                                                            label={`${stockNum} en stock (Mín: ${minStockNum})`}
                                                            size="small"
                                                            color="warning"
                                                            sx={{ fontWeight: 800, fontSize: '11px', borderRadius: '6px' }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<CheckCircleIcon sx={{ fontSize: '12px !important' }} />}
                                                            label={`${stockNum} dispon.`}
                                                            size="small"
                                                            color="success"
                                                            sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '6px' }}
                                                        />
                                                    )}
                                                </TableCell>

                                                <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>
                                                    S/ {pPrice.toFixed(2)}
                                                </TableCell>

                                                <TableCell sx={{ color: '#059669', fontWeight: 800 }}>
                                                    S/ {sPrice.toFixed(2)}
                                                </TableCell>

                                                <TableCell sx={{ color: '#7c3aed', fontWeight: 700 }}>
                                                    {rType === 'free_loan' ? (
                                                        <Chip label="Comodato Gratuito" size="small" sx={{ height: 22, fontSize: '10px', fontWeight: 700, bgcolor: '#f3e8ff', color: '#6b21a8' }} />
                                                    ) : rPrice > 0 ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#7c3aed', fontSize: '13px' }}>
                                                                S/ {rPrice.toFixed(2)} / mes
                                                            </Typography>
                                                            {rType === 'installments' && rInst && (
                                                                <Typography variant="caption" sx={{ color: '#6b21a8', fontSize: '10px', fontWeight: 600 }}>
                                                                    ({rInst} cuotas)
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>
                                                    )}
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                                        <Tooltip title="Editar Producto">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenDialog(item)}
                                                                sx={{ color: '#2563eb', bgcolor: 'rgba(37, 99, 235, 0.08)', '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.18)' } }}
                                                            >
                                                                <EditIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Eliminar Producto">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDelete(item.id)}
                                                                sx={{ color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.08)', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.18)' } }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                            <InventoryIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>
                                                No se encontraron productos
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                                                Prueba cambiando los filtros de búsqueda o registra un nuevo producto en el sistema.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => handleOpenDialog()}
                                                startIcon={<AddIcon />}
                                                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Registrar Producto
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredItems.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        sx={{ borderTop: '1px solid #f1f5f9' }}
                    />
                </Paper>
            </Box>

            {/* MOBILE VIEW: Product Cards Grid (Visible on mobile xs and sm - ZERO horizontal scroll) */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.8 }}>
                {paginatedItems.length > 0 ? (
                    paginatedItems.map((item) => {
                        const stockNum = item.total_stock || 0;
                        const minStockNum = item.min_stock || 0;
                        const isLowStock = stockNum <= minStockNum;
                        const isOutOfStock = stockNum === 0;

                        const pPrice = parseFloat(item.purchase_price) || 0;
                        const sPrice = parseFloat(item.selling_price) || 0;

                        return (
                            <Paper
                                key={item.id}
                                sx={{
                                    p: 2,
                                    borderRadius: '12px',
                                    border: isOutOfStock ? '1px solid #fca5a5' : isLowStock ? '1px solid #fde68a' : '1px solid #e2e8f0',
                                    bgcolor: isOutOfStock ? '#fef2f2' : isLowStock ? '#fffbeb' : '#fff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                                    boxSizing: 'border-box',
                                    width: '100%'
                                }}
                            >
                                {/* Top Header: Name & Stock Badge */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.25, wordBreak: 'break-word' }}>
                                            {item.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.6 }}>
                                            {item.brand && (
                                                <Chip label={item.brand} size="small" variant="outlined" sx={{ height: 20, fontSize: '10px', fontWeight: 700, borderColor: '#cbd5e1' }} />
                                            )}
                                            {item.category_name && (
                                                <Chip label={item.category_name} size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }} />
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ flexShrink: 0 }}>
                                        {isOutOfStock ? (
                                            <Chip label="Agotado (0)" size="small" color="error" sx={{ fontWeight: 800, fontSize: '10px', height: 22 }} />
                                        ) : isLowStock ? (
                                            <Chip label={`Bajo (${stockNum})`} size="small" color="warning" sx={{ fontWeight: 800, fontSize: '10px', height: 22 }} />
                                        ) : (
                                            <Chip label={`${stockNum} dispon.`} size="small" color="success" sx={{ fontWeight: 700, fontSize: '10px', height: 22 }} />
                                        )}
                                    </Box>
                                </Box>

                                {/* Specs / Price Grid */}
                                <Grid container spacing={1} sx={{ my: 0.8, py: 1, borderTop: '1px solid rgba(0,0,0,0.06)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '10px', fontWeight: 600 }}>Modelo / SKU</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', fontSize: '12px' }}>
                                            {item.model || item.sku || 'N/A'}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '10px', fontWeight: 600 }}>P. Compra / Venta</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                            <span style={{ color: '#64748b', textDecoration: 'line-through', marginRight: '6px', fontSize: '0.78rem' }}>${pPrice.toFixed(2)}</span>
                                            <span style={{ color: '#059669', fontSize: '0.9rem' }}>${sPrice.toFixed(2)}</span>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {/* Card Footer: Type & Buttons */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px' }}>
                                        {item.equipment_type ? `Tipo: ${item.equipment_type}` : 'General'}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleOpenDialog(item)}
                                            startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', py: 0.4, px: 1.2, fontSize: '12px', color: '#2563eb', borderColor: '#bfdbfe' }}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => handleDelete(item.id)}
                                            startIcon={<DeleteIcon sx={{ fontSize: '14px !important' }} />}
                                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', py: 0.4, px: 1.2, fontSize: '12px' }}
                                        >
                                            Eliminar
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })
                ) : (
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px' }}>
                        <InventoryIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569' }}>
                            Sin productos encontrados
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenDialog()}
                            startIcon={<AddIcon />}
                            sx={{ mt: 1.5, borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Nuevo Producto
                        </Button>
                    </Paper>
                )}

                {/* Mobile Pagination */}
                <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', mt: 1 }}>
                    <TablePagination
                        component="div"
                        count={filteredItems.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Filas:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                </Paper>
            </Box>

            {/* Modal Dialog for Add/Edit Product */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', margin: { xs: 1.5, sm: 2 } } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bg: '#0f172a', color: '#0f172a', borderBottom: '1px solid #e2e8f0', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <InventoryIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                            {isEditing ? 'Editar Producto' : 'Nuevo Producto de Inventario'}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ pt: 1 }}>
                        <Grid container spacing={2}>
                            {/* Nombre del Producto */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Nombre del Producto"
                                    name="name"
                                    value={currentItem.name}
                                    onChange={handleInputChange}
                                    placeholder="Ej. Router MikroTik hAP ac3"
                                />
                            </Grid>

                            {/* Categoría */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    required
                                    label="Categoría"
                                    name="category_id"
                                    value={currentItem.category_id}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="">Seleccionar categoría</MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Modelo */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Modelo"
                                    name="model"
                                    value={currentItem.model}
                                    onChange={handleInputChange}
                                    placeholder="Ej. RBD53iG-5HacD2HnD"
                                />
                            </Grid>

                            {/* Marca */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Marca"
                                    name="brand"
                                    value={currentItem.brand}
                                    onChange={handleInputChange}
                                    placeholder="Ej. MikroTik, Ubiquiti, TP-Link"
                                />
                            </Grid>

                            {/* SKU */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="SKU / Código Interno"
                                    name="sku"
                                    value={currentItem.sku}
                                    onChange={handleInputChange}
                                    placeholder="Ej. MK-HAPAC3-01"
                                />
                            </Grid>

                            {/* Código de Barras */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Código de Barras (UPC/EAN)"
                                    name="barcode"
                                    value={currentItem.barcode}
                                    onChange={handleInputChange}
                                    placeholder="Ej. 7891234567890"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 0.5 }}>
                                    <Chip label="Precios y Control de Stock" size="small" sx={{ fontWeight: 700, fontSize: '11px' }} />
                                </Divider>
                            </Grid>

                            {/* Precio de Compra */}
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Precio de Compra"
                                    name="purchase_price"
                                    value={currentItem.purchase_price}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                />
                            </Grid>

                            {/* Precio de Venta */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Precio de Venta"
                                    name="selling_price"
                                    value={currentItem.selling_price}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">S/</InputAdornment>
                                    }}
                                />
                            </Grid>

                            {/* Stock Mínimo */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label="Stock Mínimo Alerta"
                                    name="min_stock"
                                    value={currentItem.min_stock}
                                    onChange={handleInputChange}
                                />
                            </Grid>

                            {/* Precio de Alquiler */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Precio Alquiler / Cuota"
                                    name="rental_price"
                                    value={currentItem.rental_price}
                                    onChange={handleInputChange}
                                    helperText="S/ por mes o cuota"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">S/</InputAdornment>
                                    }}
                                />
                            </Grid>

                            {/* Modalidad de Alquiler */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo de Alquiler"
                                    name="rental_type"
                                    value={currentItem.rental_type || 'monthly'}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="monthly">Mensual Continuo</MenuItem>
                                    <MenuItem value="installments">En Cuotas (Meses)</MenuItem>
                                    <MenuItem value="one_time">Pago Único</MenuItem>
                                    <MenuItem value="free_loan">Comodato Gratuito</MenuItem>
                                </TextField>
                            </Grid>

                            {currentItem.rental_type === 'installments' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Número de Cuotas Mensuales"
                                        name="rental_installments"
                                        value={currentItem.rental_installments}
                                        onChange={handleInputChange}
                                        placeholder="Ej. 12, 18, 24"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 0.5 }}>
                                    <Chip label="Especificaciones Técnicas (WISP / Telecom)" size="small" sx={{ fontWeight: 700, fontSize: '11px' }} />
                                </Divider>
                            </Grid>

                            {/* Tipo de Equipo */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo de Equipo"
                                    name="equipment_type"
                                    value={currentItem.equipment_type}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="">Ninguno / Accesorios</MenuItem>
                                    <MenuItem value="Router">Router</MenuItem>
                                    <MenuItem value="CPE">CPE (Antena Cliente)</MenuItem>
                                    <MenuItem value="Antena Sectorial">Antena Sectorial</MenuItem>
                                    <MenuItem value="Antena Parabólica">Antena Parabólica</MenuItem>
                                    <MenuItem value="Switch">Switch</MenuItem>
                                    <MenuItem value="OLT">OLT</MenuItem>
                                    <MenuItem value="Otro">Otro</MenuItem>
                                </TextField>
                            </Grid>

                            {/* Frecuencia */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Frecuencia"
                                    name="frequency"
                                    value={currentItem.frequency}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="">N/A</MenuItem>
                                    <MenuItem value="2.4 GHz">2.4 GHz</MenuItem>
                                    <MenuItem value="5 GHz">5 GHz</MenuItem>
                                    <MenuItem value="60 GHz">60 GHz</MenuItem>
                                    <MenuItem value="Multi-banda">Multi-banda</MenuItem>
                                </TextField>
                            </Grid>

                            {/* Capacidad / Velocidad */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label="Capacidad / Velocidad"
                                    name="speed_capacity"
                                    value={currentItem.speed_capacity}
                                    onChange={handleInputChange}
                                    placeholder="Ej. 300 Mbps, Wi-Fi 6"
                                />
                            </Grid>

                            {/* Versión Hardware / Firmware */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label="Hardware / Firmware"
                                    name="hardware_firmware_version"
                                    value={currentItem.hardware_firmware_version}
                                    onChange={handleInputChange}
                                    placeholder="Ej. v6.48.6, v3.0"
                                />
                            </Grid>

                            {/* Descripción */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Descripción o Notas Adicionales"
                                    name="description"
                                    value={currentItem.description}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!currentItem.name || !currentItem.category_id}
                        sx={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3
                        }}
                    >
                        {isEditing ? 'Guardar Cambios' : 'Crear Producto'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventoryItems;